import {
  BoldOutlined,
  ClearOutlined,
  DeleteColumnOutlined,
  DeleteOutlined,
  DeleteRowOutlined,
  DownOutlined,
  InsertRowAboveOutlined,
  InsertRowBelowOutlined,
  InsertRowLeftOutlined,
  InsertRowRightOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { ChainedCommands, Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react'
import type { MenuProps } from 'antd'
import { Button, Dropdown, Form, Input, Popover, Tooltip } from 'antd'
import clsx from 'clsx'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { getApiErrorMessage } from 'utils/api-error'
import { logError } from 'utils/logger'
import { uploadFileToBackend } from 'utils/upload'

import { createRichTextExtensions } from './extensions'
import './style.scss'

export interface RichTextEditorProps {
  /** HTML content. An empty document is reported as `''`. */
  value?: string
  onChange?: (html: string) => void
  onBlur?: () => void
  placeholder?: string
  status?: 'error'
  minHeight?: number
}

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3'

const BLOCK_OPTIONS: { key: BlockType; label: string }[] = [
  { key: 'paragraph', label: 'Paragraph' },
  { key: 'h1', label: 'Heading 1' },
  { key: 'h2', label: 'Heading 2' },
  { key: 'h3', label: 'Heading 3' },
]

type HeadingBlock = Exclude<BlockType, 'paragraph'>

const HEADING_LEVEL: Record<HeadingBlock, 1 | 2 | 3> = { h1: 1, h2: 2, h3: 3 }

const getBlockType = (editor: Editor): BlockType =>
  (Object.keys(HEADING_LEVEL) as HeadingBlock[]).find((key) =>
    editor.isActive('heading', { level: HEADING_LEVEL[key] })
  ) ?? 'paragraph'

const normalizeHref = (href: string) =>
  /^(https?:|mailto:|tel:)/i.test(href) ? href : `https://${href}`

const getHtml = (editor: Editor) => (editor.isEmpty ? '' : editor.getHTML())

interface ToolbarButtonProps {
  title: string
  icon: ReactNode
  active?: boolean
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  title,
  icon,
  active,
  disabled,
  loading,
  onClick,
}) => (
  <Tooltip title={title} mouseEnterDelay={0.4}>
    <Button
      type="text"
      size="small"
      icon={icon}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      loading={loading}
      className={clsx('rich-text-editor__btn', active && 'rich-text-editor__btn--active')}
      // keep the editor selection while clicking toolbar buttons
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    />
  </Tooltip>
)

const Toolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      block: getBlockType(e),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      link: e.isActive('link'),
      inTable: e.isActive('table'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  })

  const run = (command: (chain: ChainedCommands) => ChainedCommands) =>
    command(editor.chain().focus()).run()

  const setBlock = (block: BlockType) => {
    if (block === 'paragraph') {
      run((c) => c.setParagraph())
    } else {
      run((c) => c.setHeading({ level: HEADING_LEVEL[block] }))
    }
  }

  const handleLinkOpenChange = (open: boolean) => {
    if (open) {
      setLinkUrl(editor.getAttributes('link').href ?? '')
    }
    setLinkOpen(open)
  }

  const applyLink = () => {
    const href = linkUrl.trim()
    if (!href) {
      run((c) => c.extendMarkRange('link').unsetLink())
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      const url = normalizeHref(href)
      run((c) =>
        c.insertContent({
          type: 'text',
          text: href,
          marks: [{ type: 'link', attrs: { href: url } }],
        })
      )
    } else {
      run((c) => c.extendMarkRange('link').setLink({ href: normalizeHref(href) }))
    }
    setLinkOpen(false)
  }

  const removeLink = () => {
    run((c) => c.extendMarkRange('link').unsetLink())
    setLinkOpen(false)
  }

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) {
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed')
      return
    }
    setUploading(true)
    try {
      const src = await uploadFileToBackend(file)
      run((c) => c.setImage({ src }))
    } catch (error: unknown) {
      logError('Error uploading editor image:', error)
      toast.error(getApiErrorMessage(error, 'Failed to upload image'))
    } finally {
      setUploading(false)
    }
  }

  const inTable = state.inTable
  const tableMenu: MenuProps['items'] = [
    {
      key: 'insert',
      label: 'Insert table',
      icon: <TableOutlined />,
      disabled: inTable,
      onClick: () => run((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true })),
    },
    { type: 'divider' },
    {
      key: 'row-before',
      label: 'Add row above',
      icon: <InsertRowAboveOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.addRowBefore()),
    },
    {
      key: 'row-after',
      label: 'Add row below',
      icon: <InsertRowBelowOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.addRowAfter()),
    },
    {
      key: 'col-before',
      label: 'Add column left',
      icon: <InsertRowLeftOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.addColumnBefore()),
    },
    {
      key: 'col-after',
      label: 'Add column right',
      icon: <InsertRowRightOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.addColumnAfter()),
    },
    {
      key: 'row-delete',
      label: 'Delete row',
      icon: <DeleteRowOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.deleteRow()),
    },
    {
      key: 'col-delete',
      label: 'Delete column',
      icon: <DeleteColumnOutlined />,
      disabled: !inTable,
      onClick: () => run((c) => c.deleteColumn()),
    },
    {
      key: 'header-row',
      label: 'Toggle header row',
      disabled: !inTable,
      onClick: () => run((c) => c.toggleHeaderRow()),
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: 'Delete table',
      icon: <DeleteOutlined />,
      danger: true,
      disabled: !inTable,
      onClick: () => run((c) => c.deleteTable()),
    },
  ]

  const blockLabel = BLOCK_OPTIONS.find((o) => o.key === state.block)?.label ?? 'Paragraph'

  return (
    <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Formatting">
      <ToolbarButton
        title="Undo"
        icon={<UndoOutlined />}
        disabled={!state.canUndo}
        onClick={() => run((c) => c.undo())}
      />
      <ToolbarButton
        title="Redo"
        icon={<RedoOutlined />}
        disabled={!state.canRedo}
        onClick={() => run((c) => c.redo())}
      />
      <span className="rich-text-editor__divider" aria-hidden />

      <Dropdown
        trigger={['click']}
        menu={{
          items: BLOCK_OPTIONS.map((o) => ({ key: o.key, label: o.label })),
          selectable: true,
          selectedKeys: [state.block],
          onClick: ({ key }) => setBlock(key as BlockType),
        }}
      >
        <Button
          type="text"
          size="small"
          className="rich-text-editor__block-select"
          onMouseDown={(e) => e.preventDefault()}
        >
          {blockLabel}
          <DownOutlined />
        </Button>
      </Dropdown>
      <span className="rich-text-editor__divider" aria-hidden />

      <ToolbarButton
        title="Bold"
        icon={<BoldOutlined />}
        active={state.bold}
        onClick={() => run((c) => c.toggleBold())}
      />
      <ToolbarButton
        title="Italic"
        icon={<ItalicOutlined />}
        active={state.italic}
        onClick={() => run((c) => c.toggleItalic())}
      />
      <ToolbarButton
        title="Underline"
        icon={<UnderlineOutlined />}
        active={state.underline}
        onClick={() => run((c) => c.toggleUnderline())}
      />
      <ToolbarButton
        title="Strikethrough"
        icon={<StrikethroughOutlined />}
        active={state.strike}
        onClick={() => run((c) => c.toggleStrike())}
      />
      <span className="rich-text-editor__divider" aria-hidden />

      <ToolbarButton
        title="Bulleted list"
        icon={<UnorderedListOutlined />}
        active={state.bulletList}
        onClick={() => run((c) => c.toggleBulletList())}
      />
      <ToolbarButton
        title="Numbered list"
        icon={<OrderedListOutlined />}
        active={state.orderedList}
        onClick={() => run((c) => c.toggleOrderedList())}
      />
      <span className="rich-text-editor__divider" aria-hidden />

      <Popover
        trigger="click"
        open={linkOpen}
        onOpenChange={handleLinkOpenChange}
        destroyTooltipOnHide
        content={
          <div className="rich-text-editor__link-form">
            <Input
              autoFocus
              size="small"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onPressEnter={applyLink}
            />
            {state.link ? (
              <Button size="small" onClick={removeLink}>
                Remove
              </Button>
            ) : null}
            <Button size="small" type="primary" onClick={applyLink}>
              Apply
            </Button>
          </div>
        }
      >
        <span>
          <ToolbarButton title="Link" icon={<LinkOutlined />} active={state.link} />
        </span>
      </Popover>
      <ToolbarButton
        title="Image"
        icon={<PictureOutlined />}
        loading={uploading}
        onClick={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void handleImageSelected(e)}
      />
      <Dropdown trigger={['click']} menu={{ items: tableMenu }}>
        <span>
          <ToolbarButton title="Table" icon={<TableOutlined />} active={inTable} />
        </span>
      </Dropdown>
      <span className="rich-text-editor__divider" aria-hidden />

      <ToolbarButton
        title="Clear formatting"
        icon={<ClearOutlined />}
        onClick={() => run((c) => c.unsetAllMarks().clearNodes())}
      />
    </div>
  )
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  status,
  minHeight = 320,
}) => {
  const onChangeRef = useRef(onChange)
  const onBlurRef = useRef(onBlur)
  onChangeRef.current = onChange
  onBlurRef.current = onBlur
  // picks up the error state when rendered inside a Form.Item
  const { status: formItemStatus } = Form.Item.useStatus()
  const hasError = (status ?? formItemStatus) === 'error'

  const editor = useEditor({
    extensions: createRichTextExtensions(placeholder),
    content: value ?? '',
    onUpdate: ({ editor: e }) => onChangeRef.current?.(getHtml(e)),
    onBlur: () => onBlurRef.current?.(),
  })

  // Sync external value changes (e.g. form reset / edit prefill) without clobbering typing
  useEffect(() => {
    if (value === undefined || value === getHtml(editor)) {
      return
    }
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  return (
    <div
      className={clsx('rich-text-editor', hasError && 'rich-text-editor--error')}
      style={{ '--rich-text-editor-min-height': `${minHeight}px` } as React.CSSProperties}
    >
      <Toolbar editor={editor} />
      <EditorContent editor={editor} className="rich-text-editor__content rich-text-content" />
    </div>
  )
}

export default RichTextEditor
